import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Button } from '@3plearning/react-components';
import get from 'lodash/get';
import {
  SearchableListWrapper,
  InputWrapper,
  SelectPanelWrapper,
  showElement,
  SearchContainer,
  FilterWrapper,
  FilteredListWrapper
} from './styled';
import { strandExpandCollapse } from '../../containers/upf-assign/upf-assign-actions';
import SvgIcon from '../svg-icon/svg-icon';
import { FilterIcon, FilterOutlineIcon, DownArrow, UpArrow, PreviewDemoIcon } from '../../helpers/icon-dictionary';
import SelectableListItem from '../selectable-list-item';
import OutsideClickHandler from '../../helpers/hoc/close/outside-click-handler';
import {
  CHECKED,
  UNCHECKED,
  INDETERMINATE
} from '../checkbox/constants';
import { getAllLearningJourneys, getSelectedTopicCount } from '../../containers/upf-assign/upf-assign-util';
import usePreview from '../../helpers/hooks/use-preview';
import { durationTypes } from '../../helpers/utils';

const SearchableDropDownList = (props) => {
  const dispatch = useDispatch();
  const { i18n } = props;
  const topics = get(props.topics, '[0].childTopics') || [];
  const { selectedTopicCount, topicCount } = getSelectedTopicCount(topics);
  const [searchTerm, setSearchTerm] = useState('');
  const searchTextbox = useRef(null);
  const label = i18n.t`Showing ${selectedTopicCount} of ${topicCount} questions, click here to filter`;
  const [isLoading, handlePreview] = usePreview(getAllLearningJourneys(props.topics, false));
  const [selectPaneHeight, setSelectPaneHeight] = useState(null);
  const selectPaneRef = useRef(null);

  const handleResize = (e) => {
    const { innerHeight } = e?.target || window;
    const { top, bottom } = selectPaneRef.current.getBoundingClientRect();
    setSelectPaneHeight(innerHeight > bottom ? bottom - top : innerHeight - top);
  };

  const handleClick = () => {
    if (topics.length) {
      props.showHideSearchPanel(true);
      searchTextbox.current.placeholder = i18n.t`Type here to search`;
      // give time for select pane to render
      setTimeout(handleResize);
    }
  };

  const handleOnChange = (e) => {
    const searchText = e.target.value;
    setSearchTerm(searchText);
    props.updateStrands('onSearch', '', false, searchText);
    if (!searchText) {
      searchTextbox.current.placeholder = i18n.t`Type here to search`;
    }
  };

  const uncheckAll = () => {
    props.updateStrands('uncheckAll');
  };

  const applyFilter = () => {
    props.showHideSearchPanel(false);
    props.applyFilter();
    searchTextbox.current.placeholder = label;
  };

  const onSelect = (id, checked) => {
    props.updateStrands('selection', id, checked, searchTerm);
  };

  const getChildTopics = item => item.childTopics.map(std => (
    <div className="spacer" key={`${item.id}#${std.id}`}>
      <SelectableListItem
        id={`${item.id}#${std.id}`}
        checked={std.selected ? CHECKED : UNCHECKED}
        name={std.displayName}
        onChange={onSelect} />
    </div>)
  );

  const onStrandIconClick = (id) => {
    dispatch(strandExpandCollapse(id));
  };

  const getCheckedState = (item) => {
    const selectedChildLength = item.childTopics.filter(childTopic => childTopic.selected).length;

    if (!selectedChildLength) return UNCHECKED;

    if (item.childTopics.length === selectedChildLength) return CHECKED;

    return INDETERMINATE;
  };

  const renderStrandItems = () => {
    if (topics) {
      return topics.map(item => (
        <React.Fragment key={item.id}>
          <div className="strand">
            <div className="wrapper">
              <div className="Description">
                <SelectableListItem
                  id={item.id}
                  checked={getCheckedState(item)}
                  name={item.displayName}
                  onChange={onSelect} />
              </div>
              <div className="icon" tabIndex={0} onClick={() => onStrandIconClick(item.id)} role="button">
                <SvgIcon icon={item.isExpand ? UpArrow : DownArrow} /></div>
            </div>
            {item.isExpand ? getChildTopics(item) : null}
          </div><hr className="line" />
        </React.Fragment>
      ));
    }

    return null;
  };

  const focusSearchTextbox = () => {
    searchTextbox.current.placeholder = label;
  };

  const logoTitle = selectedTopicCount === topicCount ?
    i18n.t`No filters have been applied - entire assessment is shown`
    : i18n.t`Filters have been applied - click search bar to see which assessments have been excluded`;

  useEffect(() => {
    addEventListener('resize', handleResize);
    return () => {
      removeEventListener('resize', handleResize);
    };
  }, []);

  return (<div>
    <OutsideClickHandler
      callback={focusSearchTextbox}
      forButton="searchPanel">
      <div id="searchPanel">
        <SearchContainer>
          <FilterWrapper>
            <label htmlFor="filter">{i18n.t`Filter`}</label>
            <SearchableListWrapper>
              <div className="searchText">
                <InputWrapper
                  ref={searchTextbox}
                  type="text"
                  onClick={handleClick}
                  onChange={handleOnChange}
                  value={searchTerm}
                  placeholder={label}
                  data-name="filter" />
              </div>
              <div className="searchLogo" title={logoTitle}>
                {selectedTopicCount === topicCount ? (<SvgIcon icon={FilterOutlineIcon} />)
                  : (<FilteredListWrapper><SvgIcon icon={FilterIcon} /></FilteredListWrapper>)}
              </div>
            </SearchableListWrapper>
          </FilterWrapper>
        </SearchContainer>
        <SelectPanelWrapper
          style={showElement(props.showSearchPanel)}
          ref={selectPaneRef}
          height={selectPaneHeight}
        >
          <div>
            <div className="panelBody">{renderStrandItems()}</div>
            <div className="panelFooter">
              <div className="uncheck"><Button
                className="unCheckAllButton"
                disabled={!topics.length}
                text={i18n.t`Unselect all`}
                type={Button.LOZENGE}
                onClick={uncheckAll}
              /></div>
              <div className="preview"><Button
                className="previewButton"
                disabled={!selectedTopicCount || isLoading}
                text={i18n.t`Preview selection`}
                type={Button.LOZENGE}
                onClick={() => handlePreview(
                  60 * 60,
                  durationTypes.PER_ASSIGNMENT,
                  false
                )}
                icon={PreviewDemoIcon}
              /></div>
              <div className="apply"><Button
                className="applyButton"
                disabled={!topics.length}
                primary
                text={i18n.t`Apply filter`}
                type={Button.LOZENGE}
                onClick={applyFilter} /></div>
            </div>
          </div>
        </SelectPanelWrapper>
      </div>
    </OutsideClickHandler>
  </div>);
};

SearchableDropDownList.propTypes = {
  topics: PropTypes.array,
  updateStrands: PropTypes.func,
  applyFilter: PropTypes.func,
  showSearchPanel: PropTypes.bool,
  showHideSearchPanel: PropTypes.func,
  i18n: PropTypes.object
};

SearchableDropDownList.defaultProps = {
  showSearchPanel: false
};

export default SearchableDropDownList;
